// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the end-of-day wrap-up (design/evening-wrapup r2). Half an hour before the person's day
// ends, Konvo posts a plan in their Konvo chat (and the corner shows the same card): agents that can
// work tonight on the person's own machines, new ClickUp tasks for the next working day, and what is
// already due then. One tap starts the agents and creates the tasks; the card then turns into
// "Tonight is set". The API (konvo/engine/wrapup) and the app (features/konvo_wrapup) both import
// these shapes.

import {z} from 'zod';
import {AgentModelChoiceSchema} from './Agents';

export const WRAP_UP_STATUSES = ['offered', 'started', 'skipped', 'undone'] as const;
export type WrapUpStatus = (typeof WRAP_UP_STATUSES)[number];

/** Where a row came from, for the line under its title. */
export const WRAP_UP_SOURCE_KINDS = ['mail', 'whatsapp', 'konvo', 'agents', 'clickup', 'promise'] as const;
export type WrapUpSourceKind = (typeof WRAP_UP_SOURCE_KINDS)[number];

export const WrapUpSourceSchema = z.object({
	kind: z.enum(WRAP_UP_SOURCE_KINDS),
	/** Who and where, e.g. "Danie Coetzee · mail, 11:05". */
	label: z.string().max(200),
	/** The words it came from, trimmed. */
	quote: z.string().max(240).nullable().default(null),
	/** The waiting item, mail, chat or agent chat it came from, so the new task can link back. */
	ref: z.string().max(600).nullable().default(null),
});
export type WrapUpSource = z.infer<typeof WrapUpSourceSchema>;

/** The ClickUp task an agent row belongs to: one that exists, or one Konvo creates on Start. */
export const WrapUpTaskRefSchema = z.discriminatedUnion('kind', [
	z.object({
		kind: z.literal('existing'),
		id: z.string().max(40),
		name: z.string().max(255),
		url: z.string().max(400).nullable().default(null),
	}),
	z.object({
		kind: z.literal('new'),
		list_id: z.string().max(40),
		list_name: z.string().max(160),
	}),
]);
export type WrapUpTaskRef = z.infer<typeof WrapUpTaskRefSchema>;

/**
 * An agent's state: `planned` until Start; then `working` (started), `scheduled` (starts at
 * `start_at`, e.g. when a used-up plan limit resets), `queued` (the machine is offline: it starts
 * when the machine is back, within 12 hours), `done`, `failed` or `stopped` (Undo).
 */
export const WRAP_UP_AGENT_STATES = ['planned', 'working', 'scheduled', 'queued', 'done', 'failed', 'stopped'] as const;
export type WrapUpAgentState = (typeof WRAP_UP_AGENT_STATES)[number];

export const WrapUpAgentRowSchema = z.object({
	id: z.string().max(20),
	title: z.string().max(160),
	/** The prompt the agent gets, exactly. */
	prompt: z.string().max(4000),
	/** One line of what the agent will do, for the card. */
	does: z.string().max(240).default(''),
	project_id: z.string().max(200),
	project_title: z.string().max(200),
	/** Always one of the person's own machines. */
	machine_id: z.string().max(200),
	machine_name: z.string().max(120),
	model: AgentModelChoiceSchema.nullable().default(null),
	/** An agent chat to carry on (one that failed or is waiting) instead of starting a new one. */
	continue_thread_id: z.string().max(200).nullable().default(null),
	task: WrapUpTaskRefSchema.nullable().default(null),
	source: WrapUpSourceSchema.nullable().default(null),
	/** E.g. "Failed today". */
	tag: z.string().max(40).nullable().default(null),
	/** Do not start before this (a plan limit that resets later). */
	start_at: z.string().max(40).nullable().default(null),
	on: z.boolean().default(true),
	state: z.enum(WRAP_UP_AGENT_STATES).default('planned'),
	/** The agent chat once started. */
	thread_id: z.string().max(200).nullable().default(null),
	started_at: z.string().max(40).nullable().default(null),
	/** The task Konvo created for it on Start. */
	created_task: z
		.object({id: z.string().max(40), url: z.string().max(400).nullable().default(null)})
		.nullable()
		.default(null),
	error: z.string().max(300).nullable().default(null),
});
export type WrapUpAgentRow = z.infer<typeof WrapUpAgentRowSchema>;

export const WrapUpTaskRowSchema = z.object({
	id: z.string().max(20),
	title: z.string().max(255),
	list_id: z.string().max(40),
	list_name: z.string().max(160),
	/** The working day it is due (YYYY-MM-DD, the person's zone). */
	due_day: z.string().max(10),
	source: WrapUpSourceSchema.nullable().default(null),
	on: z.boolean().default(true),
	created: z
		.object({id: z.string().max(40), url: z.string().max(400).nullable().default(null)})
		.nullable()
		.default(null),
	error: z.string().max(300).nullable().default(null),
});
export type WrapUpTaskRow = z.infer<typeof WrapUpTaskRowSchema>;

/** Already in ClickUp and due the next working day: shown, nothing to do. */
export const WrapUpDueSchema = z.object({
	id: z.string().max(40),
	name: z.string().max(255),
	list_name: z.string().max(160).nullable().default(null),
	url: z.string().max(400).nullable().default(null),
});
export type WrapUpDue = z.infer<typeof WrapUpDueSchema>;

export const WrapUpMachineSchema = z.object({
	id: z.string().max(200),
	name: z.string().max(120),
	platform: z.string().max(20),
	online: z.boolean(),
	/** Projects this machine shares with Konvo (a row may only move to a machine that has its project). */
	projects: z.array(z.string().max(200)).max(200),
});
export type WrapUpMachine = z.infer<typeof WrapUpMachineSchema>;

export const WrapUpPlanSchema = z.object({
	id: z.string().max(40),
	/** The person's local day it was made for (YYYY-MM-DD). */
	day: z.string().max(10),
	status: z.enum(WRAP_UP_STATUSES),
	created_at: z.string().max(40),
	/** The next working day (YYYY-MM-DD) and its weekday, e.g. "Friday". */
	next_day: z.string().max(10),
	next_day_label: z.string().max(40),
	/** Why tomorrow is not the next working day, e.g. "Heritage Day". */
	holiday: z.string().max(120).nullable().default(null),
	agents: z.array(WrapUpAgentRowSchema).max(8),
	tasks: z.array(WrapUpTaskRowSchema).max(10),
	due: z.array(WrapUpDueSchema).max(10),
	/** The person's own machines, for the machine picker. */
	machines: z.array(WrapUpMachineSchema).max(20),
	started_at: z.string().max(40).nullable().default(null),
	/** Undo works until then. */
	undo_until: z.string().max(40).nullable().default(null),
	/** When the agents digest will report on tonight, if the person has it on. */
	digest_at: z.string().max(40).nullable().default(null),
	/** The plan's message in the person's Konvo chat. */
	channel_id: z.string().max(40).nullable().default(null),
	message_id: z.string().max(40).nullable().default(null),
});
export type WrapUpPlan = z.infer<typeof WrapUpPlanSchema>;

/** PATCH …/wrap-up/:id: tick or untick rows, or move an agent to another of the person's machines. */
export const WrapUpEditSchema = z.object({
	rows: z
		.array(
			z.object({
				id: z.string().max(20),
				on: z.boolean().optional(),
				machine_id: z.string().max(200).optional(),
			}),
		)
		.max(20),
});
export type WrapUpEdit = z.infer<typeof WrapUpEditSchema>;

/** How many tasks Start creates: the ticked task rows and the ticked agents that need a new task. */
export function wrapUpCounts(plan: Pick<WrapUpPlan, 'agents' | 'tasks'>): {agents: number; tasks: number} {
	const agents = plan.agents.filter((row) => row.on);
	return {
		agents: agents.length,
		tasks: plan.tasks.filter((row) => row.on).length + agents.filter((row) => row.task?.kind === 'new').length,
	};
}
